import { useEffect, useState } from "react";
import { useLocalSearchParams, useRouter } from "expo-router";
import { StyleSheet, View } from "react-native";

import {
  AppText,
  Button,
  ButtonText,
  Card,
  DetailRow,
  EmptyState,
  ProtectedScreen,
  SearchableSelect,
  SectionHeader,
  Stack,
  TextField,
} from "~/components/ui";
import {
  usePrimaryWorkspace,
  useWorkspaceClients,
  useWorkspaceInvoices,
  useWorkspaceProjectStatuses,
  useWorkspaceProjects,
  useWorkspaceQuotes,
  useWorkspaceTasks,
  useWorkspaceTimeEntries,
} from "~/hooks/workspace";
import {
  radii,
  spacing,
  useTheme,
  useThemedStyles,
  type ThemeColors,
} from "~/styles/theme";
import { api } from "~/api/client";
import {
  TASK_PRIORITY_OPTIONS,
  projectBudgetTypeLabel,
  taskPriorityLabel,
} from "~/utils/work-planning";

function normalizeParam(value: string | string[] | undefined) {
  return Array.isArray(value) ? value[0] : value;
}

function formatMoney(cents: number | null | undefined) {
  if (!cents) return "$0.00";
  return `$${(cents / 100).toFixed(2)}`;
}

function formatHours(seconds: number) {
  const hours = seconds / 3600;
  return `${hours.toFixed(hours >= 10 ? 0 : 1)}h`;
}

function billableValue(entry: {
  duration_seconds: number;
  hourly_rate_cents: number | null;
}) {
  if (!entry.hourly_rate_cents) return 0;
  return Math.round((entry.duration_seconds / 3600) * entry.hourly_rate_cents);
}

const BILLING_OPTIONS = [
  { label: "Billable", value: "true" },
  { label: "Non-billable", value: "false" },
];

export default function ProjectDetailScreen() {
  const { colors } = useTheme();
  const stylesheet = useThemedStyles(makeStyles);
  const router = useRouter();
  const params = useLocalSearchParams<{ id?: string | string[] }>();
  const projectId = normalizeParam(params.id);
  const [taskName, setTaskName] = useState("");
  const [taskDueOn, setTaskDueOn] = useState("");
  const [taskDescription, setTaskDescription] = useState("");
  const [taskPriority, setTaskPriority] = useState("medium");
  const [timeDescription, setTimeDescription] = useState("");
  const [timeMinutes, setTimeMinutes] = useState("60");
  const [timeHourlyRate, setTimeHourlyRate] = useState("");
  const [timeTaskId, setTimeTaskId] = useState("");
  const [timeBillable, setTimeBillable] = useState("true");
  const [alertThreshold, setAlertThreshold] = useState("80");
  const workspace = usePrimaryWorkspace();
  const clients = useWorkspaceClients(workspace.data?.id);
  const projects = useWorkspaceProjects(workspace.data?.id);
  const statuses = useWorkspaceProjectStatuses(workspace.data?.id);
  const tasks = useWorkspaceTasks(workspace.data?.id);
  const timeEntries = useWorkspaceTimeEntries(workspace.data?.id);
  const invoices = useWorkspaceInvoices(workspace.data?.id);
  const quotes = useWorkspaceQuotes(workspace.data?.id);

  const project =
    (projects.data ?? []).find((record) => record.id === projectId) ?? null;
  const client = project?.client_id
    ? (clients.data ?? []).find((record) => record.id === project.client_id)
    : null;
  const status = project?.status_id
    ? (statuses.data ?? []).find((record) => record.id === project.status_id)
    : null;
  const projectTasks = (tasks.data ?? []).filter(
    (task) => task.project_id === project?.id,
  );
  const projectTaskOptions = [
    { label: "No task", value: "" },
    ...projectTasks.map((task) => ({ label: task.name, value: task.id })),
  ];
  const projectTimeEntries = (timeEntries.data ?? []).filter(
    (entry) => entry.project_id === project?.id,
  );
  const projectInvoices = (invoices.data ?? []).filter(
    (invoice) => invoice.client_id === project?.client_id,
  );
  useEffect(() => {
    if (project) {
      setAlertThreshold(String(project.budget_alert_threshold_pct ?? 80));
    }
  }, [project?.id, project?.budget_alert_threshold_pct]);
  const openTasks = projectTasks.filter(
    (task) => task.status !== "done" && task.status !== "archived",
  );
  const doneTasks = projectTasks.filter((task) => task.status === "done");
  const trackedSeconds = projectTimeEntries.reduce(
    (total, entry) => total + entry.duration_seconds,
    0,
  );
  const billableSeconds = projectTimeEntries
    .filter((entry) => entry.billable)
    .reduce((total, entry) => total + entry.duration_seconds, 0);
  const unbilledCents = projectTimeEntries
    .filter((entry) => entry.billable && !entry.invoiced_at)
    .reduce((total, entry) => total + billableValue(entry), 0);
  const invoicedCents = projectTimeEntries
    .filter((entry) => entry.invoiced_at)
    .reduce((total, entry) => total + billableValue(entry), 0);
  const budgetRemaining =
    project?.budget_type === "hours_cap" ||
    project?.budget_cents === null ||
    project?.budget_cents === undefined
      ? null
      : Math.max(0, project.budget_cents - invoicedCents - unbilledCents);
  const budgetHoursRemaining =
    project?.budget_type !== "hours_cap" || project.budget_hours === null
      ? null
      : Math.max(0, project.budget_hours - trackedSeconds / 3600);
  const budgetUsedRatio =
    project?.budget_type === "hours_cap" && project.budget_hours
      ? trackedSeconds / 3600 / project.budget_hours
      : project?.budget_cents
        ? (invoicedCents + unbilledCents) / project.budget_cents
        : null;
  const budgetUsedPercent =
    budgetUsedRatio === null ? null : Math.round(budgetUsedRatio * 100);
  const alertThresholdPct = project?.budget_alert_threshold_pct ?? 80;
  const projectQuotes = (quotes.quotes ?? []).filter(
    (quote) => quote.project_id === project?.id,
  );
  const estimateQuotes = projectQuotes.filter((quote) =>
    ["accepted", "converted", "sent", "viewed"].includes(quote.status),
  );
  const estimateCents = estimateQuotes.reduce(
    (total, quote) => total + quote.total_cents,
    0,
  );
  const actualBillableCents = invoicedCents + unbilledCents;
  const estimateDeltaCents = estimateCents - actualBillableCents;
  const error =
    workspace.error ??
    clients.error ??
    projects.error ??
    projects.setArchived.error ??
    projects.updateStatus.error ??
    statuses.error ??
    tasks.error ??
    tasks.create.error ??
    timeEntries.error ??
    timeEntries.create.error ??
    invoices.error;
  const errorMessage = error instanceof Error ? error.message : "";

  async function createProjectTask() {
    if (!project || !taskName.trim()) return;
    await tasks.create.mutateAsync({
      description: taskDescription,
      dueOn: taskDueOn,
      name: taskName,
      priority: taskPriority,
      projectId: project.id,
    });
    setTaskName("");
    setTaskDueOn("");
    setTaskDescription("");
    setTaskPriority("medium");
  }

  async function createProjectTimeEntry() {
    if (!project || !timeDescription.trim()) return;
    await timeEntries.create.mutateAsync({
      billable: timeBillable,
      clientId: project.client_id ?? "",
      description: timeDescription,
      hourlyRate: timeBillable === "true" ? timeHourlyRate.trim() : "",
      minutes: timeMinutes,
      projectId: project.id,
      taskId: timeTaskId,
    });
    setTimeDescription("");
    setTimeMinutes("60");
    setTimeHourlyRate("");
    setTimeTaskId("");
    setTimeBillable("true");
    if (workspace.data?.id) {
      void api.evaluateBudgetAlerts({
        body: { workspace_id: workspace.data.id },
      });
    }
  }

  if (workspace.isLoading || projects.isLoading) {
    return (
      <ProtectedScreen title="Project" subtitle="Loading project details.">
        <Card gap="sm">
          <AppText>Loading...</AppText>
        </Card>
      </ProtectedScreen>
    );
  }

  if (!project) {
    return (
      <ProtectedScreen title="Project not found">
        <Card gap="md">
          <AppText size="sm">
            This project could not be found in the current workspace.
          </AppText>
          <Button
            align="start"
            variant="outline"
            onPress={() => router.push("/projects" as never)}
          >
            <ButtonText>Back to projects</ButtonText>
          </Button>
        </Card>
      </ProtectedScreen>
    );
  }

  return (
    <ProtectedScreen
      title={project.name}
      subtitle="Review project scope, client context, tasks, time, and billing readiness."
    >
      {errorMessage ? (
        <Card danger gap="xs">
          <AppText tone="danger" weight="semibold">
            Project details could not load
          </AppText>
          <AppText tone="danger" size="xs">
            {errorMessage}
          </AppText>
        </Card>
      ) : null}

      <View style={stylesheet.summaryGrid}>
        <Card gap="xs" style={stylesheet.summaryCard}>
          <AppText size="xs">Status</AppText>
          <AppText size="lg" weight="bold">
            {status?.name ?? (project.archived ? "Archived" : "Open")}
          </AppText>
        </Card>
        <Card gap="xs" style={stylesheet.summaryCard}>
          <AppText size="xs">Tracked</AppText>
          <AppText size="lg" weight="bold">
            {formatHours(trackedSeconds)}
          </AppText>
        </Card>
        <Card gap="xs" style={stylesheet.summaryCard}>
          <AppText size="xs">Unbilled</AppText>
          <AppText size="lg" weight="bold">
            {formatMoney(unbilledCents)}
          </AppText>
        </Card>
        <Card gap="xs" style={stylesheet.summaryCard}>
          <AppText size="xs">Open tasks</AppText>
          <AppText size="lg" weight="bold">
            {String(openTasks.length)}
          </AppText>
        </Card>
      </View>

      <Card gap="md">
        <SectionHeader
          title="Project actions"
          description="Move the project, add work, and capture billable time from this project record."
        />
        <View style={stylesheet.actions}>
          <Button
            align="start"
            compact
            iconName={project.archived ? "archive-restore" : "archive"}
            variant="outline"
            disabled={projects.setArchived.isPending}
            onPress={() =>
              void projects.setArchived.mutateAsync({
                id: project.id,
                archived: !project.archived,
              })
            }
          >
            <ButtonText>
              {project.archived ? "Restore project" : "Archive project"}
            </ButtonText>
          </Button>
          {statuses.data?.map((projectStatus) => (
            <Button
              key={projectStatus.id}
              compact
              variant={
                project.status_id === projectStatus.id ? "primary" : "outline"
              }
              disabled={projects.updateStatus.isPending}
              onPress={() =>
                void projects.updateStatus.mutateAsync({
                  id: project.id,
                  statusId: projectStatus.id,
                })
              }
            >
              <ButtonText>{projectStatus.name}</ButtonText>
            </Button>
          ))}
        </View>
        <View style={stylesheet.actionGrid}>
          <View style={stylesheet.actionPanel}>
            <SectionHeader
              title="New task"
              description="Add a delivery item already linked to this project."
            />
            <View style={stylesheet.formGrid}>
              <View style={[stylesheet.field, stylesheet.fullWidth]}>
                <AppText size="xs" weight="semibold">
                  Task name
                </AppText>
                <TextField
                  value={taskName}
                  placeholder="Draft proposal updates"
                  onChangeText={setTaskName}
                />
              </View>
              <View style={stylesheet.field}>
                <AppText size="xs" weight="semibold">
                  Priority
                </AppText>
                <SearchableSelect
                  value={taskPriority}
                  options={TASK_PRIORITY_OPTIONS}
                  title="Choose priority"
                  onValueChange={setTaskPriority}
                />
              </View>
              <View style={stylesheet.field}>
                <AppText size="xs" weight="semibold">
                  Due date
                </AppText>
                <TextField
                  value={taskDueOn}
                  placeholder="2026-07-20"
                  onChangeText={setTaskDueOn}
                />
              </View>
              <View style={[stylesheet.field, stylesheet.fullWidth]}>
                <AppText size="xs" weight="semibold">
                  Description
                </AppText>
                <TextField
                  value={taskDescription}
                  placeholder="Acceptance notes or handover context"
                  onChangeText={setTaskDescription}
                />
              </View>
            </View>
            <Button
              align="start"
              compact
              iconName="list-plus"
              disabled={!taskName.trim() || tasks.create.isPending}
              onPress={() => void createProjectTask()}
            >
              <ButtonText>
                {tasks.create.isPending ? "Creating..." : "Create task"}
              </ButtonText>
            </Button>
          </View>

          <View style={stylesheet.actionPanel}>
            <SectionHeader
              title="Log time"
              description="Save manual time against this project and client."
            />
            <View style={stylesheet.formGrid}>
              <View style={[stylesheet.field, stylesheet.fullWidth]}>
                <AppText size="xs" weight="semibold">
                  Description
                </AppText>
                <TextField
                  value={timeDescription}
                  placeholder="Design review, coding sprint, client call..."
                  onChangeText={setTimeDescription}
                />
              </View>
              <View style={stylesheet.field}>
                <AppText size="xs" weight="semibold">
                  Minutes
                </AppText>
                <TextField
                  value={timeMinutes}
                  placeholder="60"
                  keyboardType="numeric"
                  onChangeText={setTimeMinutes}
                />
              </View>
              <View style={stylesheet.field}>
                <AppText size="xs" weight="semibold">
                  Task
                </AppText>
                <SearchableSelect
                  value={timeTaskId}
                  options={projectTaskOptions}
                  title="Choose task"
                  placeholder="No task"
                  onValueChange={(value) => {
                    setTimeTaskId(value);
                    const selectedTask = projectTasks.find(
                      (task) => task.id === value,
                    );
                    if (selectedTask) {
                      setTimeBillable(selectedTask.billable ? "true" : "false");
                    }
                  }}
                />
              </View>
              <View style={stylesheet.field}>
                <AppText size="xs" weight="semibold">
                  Billing
                </AppText>
                <SearchableSelect
                  value={timeBillable}
                  options={BILLING_OPTIONS}
                  title="Choose billing state"
                  onValueChange={setTimeBillable}
                />
              </View>
              <View style={stylesheet.field}>
                <AppText size="xs" weight="semibold">
                  Hourly rate
                </AppText>
                <TextField
                  value={timeHourlyRate}
                  placeholder="Use matching preset"
                  editable={timeBillable === "true"}
                  keyboardType="numeric"
                  muted={timeBillable !== "true"}
                  onChangeText={setTimeHourlyRate}
                />
              </View>
            </View>
            <Button
              align="start"
              compact
              iconName="clock-plus"
              disabled={!timeDescription.trim() || timeEntries.create.isPending}
              onPress={() => void createProjectTimeEntry()}
            >
              <ButtonText>
                {timeEntries.create.isPending ? "Saving..." : "Log time"}
              </ButtonText>
            </Button>
          </View>
        </View>
      </Card>

      <View style={stylesheet.detailGrid}>
        <Card gap="md" style={stylesheet.detailCard}>
          <SectionHeader
            title="Project context"
            description="Scope, dates, client, rate, and budget context."
          />
          <Stack gap="sm">
            <DetailRow label="Client" value={client?.name ?? "No client"} />
            <DetailRow label="Start" value={project.starts_on ?? "Not set"} />
            <DetailRow label="End" value={project.ends_on ?? "Not set"} />
            <DetailRow
              label="Hourly rate"
              value={
                project.hourly_rate_cents
                  ? `${formatMoney(project.hourly_rate_cents)}/h`
                  : "Not set"
              }
            />
          </Stack>
          {project.description ? (
            <View style={stylesheet.noteBlock}>
              <AppText size="xs" weight="semibold">
                Description
              </AppText>
              <AppText size="sm">{project.description}</AppText>
            </View>
          ) : null}
        </Card>

        <Card gap="md" style={stylesheet.detailCard}>
          <SectionHeader
            title="Budget and billing"
            description="Planning totals from tracked project time, with alerts when you near the cap."
          />
          {budgetUsedPercent !== null ? (
            <View style={stylesheet.budgetProgress}>
              <View style={stylesheet.budgetProgressCopy}>
                <AppText size="xs" weight="semibold">
                  {budgetUsedPercent >= 100
                    ? "Budget reached"
                    : budgetUsedPercent >= alertThresholdPct
                      ? "Budget alert threshold reached"
                      : "Budget progress"}
                </AppText>
                <AppText
                  size="xs"
                  tone={budgetUsedPercent >= 100 ? "danger" : "default"}
                >
                  {budgetUsedPercent}% used (alert at {alertThresholdPct}%)
                </AppText>
              </View>
              <View style={stylesheet.budgetTrack}>
                <View
                  style={[
                    stylesheet.budgetFill,
                    {
                      width: `${Math.min(100, budgetUsedPercent)}%`,
                      backgroundColor:
                        budgetUsedPercent >= 100
                          ? colors.danger
                          : budgetUsedPercent >= alertThresholdPct
                            ? colors.warning
                            : colors.success,
                    },
                  ]}
                />
              </View>
            </View>
          ) : null}
          <Stack gap="sm">
            <DetailRow
              label="Budget type"
              value={projectBudgetTypeLabel(project.budget_type)}
            />
            <DetailRow
              label={
                project.budget_type === "hours_cap" ? "Hours cap" : "Fee budget"
              }
              value={
                project.budget_type === "hours_cap"
                  ? project.budget_hours
                    ? `${project.budget_hours}h`
                    : "Not set"
                  : project.budget_cents
                    ? formatMoney(project.budget_cents)
                    : "Not set"
              }
            />
            <DetailRow
              label="Invoiced time value"
              value={formatMoney(invoicedCents)}
            />
            <DetailRow
              label="Unbilled time value"
              value={formatMoney(unbilledCents)}
            />
            <DetailRow
              label={
                project.budget_type === "hours_cap"
                  ? "Hours remaining"
                  : "Budget remaining"
              }
              value={
                project.budget_type === "hours_cap"
                  ? budgetHoursRemaining === null
                    ? "Not set"
                    : `${budgetHoursRemaining.toFixed(1)}h`
                  : budgetRemaining === null
                    ? "Not set"
                    : formatMoney(budgetRemaining)
              }
            />
            <DetailRow
              label="Tracked time"
              value={formatHours(trackedSeconds)}
            />
          </Stack>
          <View style={stylesheet.inlineField}>
            <AppText size="xs" weight="semibold">
              Alert threshold (%)
            </AppText>
            <TextField
              value={alertThreshold}
              placeholder="80"
              keyboardType="numeric"
              accessibilityLabel="Alert threshold percent"
              onChangeText={setAlertThreshold}
            />
            <Button
              compact
              variant="outline"
              disabled={projects.updateBudgetAlertThreshold.isPending}
              onPress={() => {
                const threshold = Number(alertThreshold);
                if (
                  !Number.isFinite(threshold) ||
                  threshold < 1 ||
                  threshold > 100
                ) {
                  return;
                }
                void projects.updateBudgetAlertThreshold.mutateAsync({
                  id: project.id,
                  thresholdPct: threshold,
                });
              }}
            >
              <ButtonText>
                {projects.updateBudgetAlertThreshold.isPending
                  ? "Saving..."
                  : "Save alert threshold"}
              </ButtonText>
            </Button>
          </View>
        </Card>

        <Card gap="md" style={stylesheet.detailCard}>
          <SectionHeader
            title="Estimate vs actual"
            description="Compare linked quote totals with billable time tracked on this project."
          />
          {estimateQuotes.length === 0 ? (
            <EmptyState title="No quotes linked to this project yet." />
          ) : (
            <Stack gap="sm">
              <DetailRow
                label="Quote estimate"
                value={formatMoney(estimateCents)}
              />
              <DetailRow
                label="Actual billable"
                value={formatMoney(actualBillableCents)}
              />
              <DetailRow
                label="Variance"
                value={`${estimateDeltaCents >= 0 ? "+" : "-"}${formatMoney(
                  Math.abs(estimateDeltaCents),
                )} ${estimateDeltaCents >= 0 ? "under" : "over"} estimate`}
              />
              <DetailRow
                label="Quotes counted"
                value={String(estimateQuotes.length)}
              />
              {estimateQuotes.slice(0, 5).map((quote) => (
                <DetailRow
                  key={quote.id}
                  label={quote.quote_number}
                  value={`${quote.status} · ${formatMoney(quote.total_cents)}`}
                />
              ))}
            </Stack>
          )}
        </Card>
      </View>

      <View style={stylesheet.detailGrid}>
        <Card gap="md" style={stylesheet.detailCard}>
          <SectionHeader
            title="Tasks"
            description="Open and completed task state for this project."
          />
          {projectTasks.length === 0 ? (
            <EmptyState title="No tasks are linked to this project." />
          ) : (
            <Stack gap="sm">
              {projectTasks.slice(0, 10).map((task) => (
                <View key={task.id} style={stylesheet.itemRow}>
                  <View style={stylesheet.itemMain}>
                    <AppText weight="semibold">{task.name}</AppText>
                    <AppText size="xs">
                      {taskPriorityLabel(task.priority)} - {task.status} -{" "}
                      {task.due_on ?? "No due date"}
                    </AppText>
                  </View>
                  <View style={stylesheet.itemActions}>
                    <AppText size="xs" weight="semibold">
                      {task.billable ? "Billable" : "Non-billable"}
                    </AppText>
                    <Button
                      compact
                      variant="outline"
                      onPress={() => router.push(`/tasks/${task.id}` as never)}
                    >
                      <ButtonText>View</ButtonText>
                    </Button>
                  </View>
                </View>
              ))}
            </Stack>
          )}
          <DetailRow label="Completed" value={String(doneTasks.length)} />
        </Card>

        <Card gap="md" style={stylesheet.detailCard}>
          <SectionHeader
            title="Time"
            description="Tracked time mix for this project."
          />
          <Stack gap="sm">
            <DetailRow
              label="All tracked"
              value={formatHours(trackedSeconds)}
            />
            <DetailRow
              label="Billable tracked"
              value={formatHours(billableSeconds)}
            />
            <DetailRow
              label="Saved entries"
              value={String(projectTimeEntries.length)}
            />
          </Stack>
          {projectTimeEntries.length > 0 ? (
            <Stack gap="sm">
              {projectTimeEntries.slice(0, 6).map((entry) => (
                <View key={entry.id} style={stylesheet.itemRow}>
                  <View style={stylesheet.itemMain}>
                    <AppText weight="semibold">
                      {entry.description ?? "Tracked time"}
                    </AppText>
                    <AppText size="xs">
                      {formatHours(entry.duration_seconds)} -{" "}
                      {entry.invoiced_at ? "Invoiced" : "Unbilled"}
                    </AppText>
                  </View>
                  <View style={stylesheet.itemActions}>
                    <AppText size="xs" weight="semibold">
                      {entry.billable
                        ? formatMoney(billableValue(entry))
                        : "Non-billable"}
                    </AppText>
                    <Button
                      compact
                      variant="outline"
                      onPress={() =>
                        router.push(`/time-entries/${entry.id}` as never)
                      }
                    >
                      <ButtonText>View</ButtonText>
                    </Button>
                  </View>
                </View>
              ))}
            </Stack>
          ) : null}
        </Card>
      </View>

      {client ? (
        <Card gap="md">
          <SectionHeader
            title="Client invoices"
            description={`Recent invoices for ${client.name}; use invoice details for exact line-item review.`}
          />
          {projectInvoices.length === 0 ? (
            <EmptyState title="No invoices exist for this client yet." />
          ) : (
            <Stack gap="sm">
              {projectInvoices.slice(0, 6).map((invoice) => (
                <View key={invoice.id} style={stylesheet.itemRow}>
                  <View style={stylesheet.itemMain}>
                    <AppText weight="semibold">
                      {invoice.invoice_number}
                    </AppText>
                    <AppText size="xs">
                      {invoice.status} - due {invoice.due_date ?? "not set"}
                    </AppText>
                  </View>
                  <View style={stylesheet.itemActions}>
                    <AppText size="xs" weight="semibold">
                      {formatMoney(invoice.total_cents)}
                    </AppText>
                    <Button
                      compact
                      variant="outline"
                      onPress={() =>
                        router.push(`/invoices/${invoice.id}` as never)
                      }
                    >
                      <ButtonText>View</ButtonText>
                    </Button>
                  </View>
                </View>
              ))}
            </Stack>
          )}
        </Card>
      ) : null}

      <View style={stylesheet.actions}>
        <Button
          align="start"
          variant="outline"
          onPress={() => router.push("/projects" as never)}
        >
          <ButtonText>Back to projects</ButtonText>
        </Button>
      </View>
    </ProtectedScreen>
  );
}

const makeStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    summaryCard: { flexGrow: 1, flexShrink: 1, flexBasis: 150, minWidth: 140 },
    detailCard: { flexGrow: 1, flexShrink: 1, flexBasis: 320, minWidth: 260 },
    summaryGrid: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: spacing.m,
    },
    detailGrid: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: spacing.m,
    },
    actionGrid: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: spacing.m,
    },
    actionPanel: {
      minWidth: 280,
      flex: 1,
      gap: spacing.m,
      padding: spacing.m,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radii.m,
      backgroundColor: colors.background,
    },
    formGrid: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: spacing.s,
    },
    field: {
      minWidth: 160,
      flex: 1,
      gap: spacing.xs,
    },
    fullWidth: {
      flexBasis: "100%",
    },
    noteBlock: {
      gap: spacing.xs,
      padding: spacing.m,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radii.m,
      backgroundColor: colors.surface,
    },
    budgetProgress: { gap: spacing.xs },
    budgetProgressCopy: {
      flexDirection: "row",
      justifyContent: "space-between",
      gap: spacing.s,
    },
    budgetTrack: {
      height: 8,
      overflow: "hidden",
      borderRadius: 999,
      backgroundColor: colors.surface,
    },
    budgetFill: { height: "100%", borderRadius: 999 },
    inlineField: {
      gap: spacing.xs,
      marginTop: spacing.s,
    },
    itemRow: {
      flexDirection: "row",
      flexWrap: "wrap",
      alignItems: "center",
      justifyContent: "space-between",
      gap: spacing.m,
      padding: spacing.m,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radii.m,
      backgroundColor: colors.background,
    },
    itemMain: {
      flex: 1,
      minWidth: 220,
      gap: spacing.xs,
    },
    itemActions: {
      alignItems: "flex-end",
      gap: spacing.s,
    },
    actions: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: spacing.s,
    },
  });
